import * as React from 'react';
import { cn } from '../lib/cn';
import { Input } from '../primitives/Input';
import { FormField } from '../primitives/FormField';

/**
 * DateRangeField — a from/to date pair.
 *
 * THE FIRST DATE CONTROL IN THE SYSTEM. Before this, `@afrilink/ui` shipped 52
 * components and not one of them accepted a date, so six pages had each
 * hand-rolled their own: raw `<input type="date">` pairs in AuditLogPage,
 * NotificationsLogPage and StatementsPage, a native `<select>` of months in
 * SalesCommissionPage, a Button chip group in CustomerBillingTab, and Radix
 * `Select`s in ServicesReportPage. Those should migrate onto this.
 *
 * Built on the `Input` primitive rather than a bare element so the 40px height,
 * the neutral-300 border and the focus ring match every other control on the
 * page.
 *
 * Controlled and presentational: it validates that `from <= to` and reports it,
 * but it never silently swaps or clamps the dates — a control that quietly
 * rewrites what you typed is worse than one that tells you it is wrong.
 */

export interface DateRangeFieldProps {
  /** ISO `YYYY-MM-DD`. */
  from?: string;
  to?: string;
  onChange: (next: { from?: string; to?: string }) => void;
  /** Externally supplied error. An internal from>to error is shown regardless. */
  error?: string;
  /** Disambiguates the input ids when more than one pair is on a page. */
  idPrefix?: string;
  className?: string;
}

function DateRangeField({
  from,
  to,
  onChange,
  error,
  idPrefix = 'range',
  className,
}: DateRangeFieldProps) {
  const inverted = !!from && !!to && from > to;
  const message = error ?? (inverted ? 'The "from" date is after the "to" date.' : undefined);

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex flex-wrap items-start gap-3">
        <FormField label="From" htmlFor={`${idPrefix}-from`} className="min-w-[9.5rem] flex-1">
          <Input
            id={`${idPrefix}-from`}
            type="date"
            value={from ?? ''}
            max={to || undefined}
            aria-invalid={inverted || undefined}
            onChange={(e) => onChange({ from: e.target.value || undefined, to })}
          />
        </FormField>
        <FormField label="To" htmlFor={`${idPrefix}-to`} className="min-w-[9.5rem] flex-1">
          <Input
            id={`${idPrefix}-to`}
            type="date"
            value={to ?? ''}
            min={from || undefined}
            aria-invalid={inverted || undefined}
            onChange={(e) => onChange({ from, to: e.target.value || undefined })}
          />
        </FormField>
      </div>

      {/* One message for the pair. FormField would render it under each input,
          which reads as two separate faults rather than one bad range. */}
      {message && (
        <p className="text-xs text-danger" role="alert">
          {message}
        </p>
      )}
    </div>
  );
}

export { DateRangeField };
